import { analytics, logEvent } from "../firebaseConfig";

/**
 * Pushes events to GTM dataLayer and Firebase Analytics simultaneously.
 * Dual tracking ensures data reaches both Google Ads (via GTM) and Firebase.
 */

function pushToDataLayer(eventName, params = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...params });
}

function trackEvent(eventName, params = {}) {
  try {
    // Firebase Analytics
    logEvent(analytics, eventName, params);
  } catch {
    // Analytics may be blocked by ad blockers
  }
  // GTM dataLayer
  pushToDataLayer(eventName, params);
}

// ── Conversion Events (high value for Google Ads) ──

export function trackContactFormSubmit() {
  trackEvent("cta_contact_click", {
    event_category: "conversion",
    event_label: "contact_form",
    value: 1,
    currency: "ARS",
  });
  // Google Ads conversion tracking
  if (typeof gtag === "function") {
    gtag("event", "conversion", {
      send_to: "AW-18096708761/UOfhCPyj2J8cEJm5l7VD",
      value: 1.0,
      currency: "ARS",
    });
  }
}

export function trackExternalLinkClick(platform, url) {
  trackEvent("click", {
    event_category: "outbound",
    event_label: platform,
    link_url: url,
    value: 3,
  });
}

// ── Interaction Events (UX insights) ──

/**
 * Tracks a click on any meaningful button/CTA across the site.
 * @param {string} label Unique button identifier (e.g. "hero_cta_contact").
 * @param {string} [location] Section/screen where the click happened.
 */
export function trackButtonClick(label, location = "") {
  trackEvent("button_click", {
    event_category: "interaction",
    event_label: label,
    location,
  });
}

/**
 * Tracks when a project card becomes visible (which projects users see most).
 * @param {string} projectName
 */
export function trackProjectView(projectName) {
  trackEvent("project_view", {
    event_category: "engagement",
    event_label: projectName,
  });
}

/**
 * Tracks how long a user actively viewed a given project card.
 * @param {string} projectName
 * @param {number} seconds
 */
export function trackProjectTime(projectName, seconds) {
  if (!projectName || seconds < 1) return;
  trackEvent("project_view_time", {
    event_category: "engagement",
    event_label: projectName,
    value: Math.round(seconds),
  });
}

/**
 * Tracks how long a user actively spent on a given section.
 * @param {string} sectionId
 * @param {number} seconds
 */
export function trackSectionTime(sectionId, seconds) {
  if (!sectionId || seconds < 1) return;
  trackEvent("section_time", {
    event_category: "engagement",
    event_label: sectionId,
    value: Math.round(seconds),
  });
}

export function trackWhatsAppClick(source = "whatsapp_floating_button") {
  trackEvent("whatsapp_click", {
    event_category: "conversion",
    event_label: source,
    value: 1,
    currency: "ARS",
  });
  // Google Ads conversion tracking
  if (typeof gtag === "function") {
    gtag("event", "conversion", {
      send_to: "AW-18096708761/Q6jfCMH8vZ8cEJm5l7VD",
      value: 1.0,
      currency: "ARS",
    });
  }
}

/**
 * Conversion for landing-page lead forms (Google Ads primary conversion).
 * @param {string} source Landing identifier (e.g. "automotoras").
 * @param {object} [params] Extra qualification data (company size, etc.).
 */
export function trackLeadSubmit(source, params = {}) {
  trackEvent("generate_lead", {
    event_category: "conversion",
    event_label: source,
    value: 1,
    currency: "ARS",
    ...params,
  });
  if (typeof gtag === "function") {
    gtag("event", "conversion", {
      send_to: "AW-18096708761/UOfhCPyj2J8cEJm5l7VD",
      value: 1.0,
      currency: "ARS",
    });
  }
}

// ── Engagement Events (micro-conversions) ──

export function trackSectionView(sectionId) {
  trackEvent("section_view", {
    event_category: "engagement",
    event_label: sectionId,
  });
}

export function trackCertificationView() {
  trackEvent("view_certifications", {
    event_category: "engagement",
    event_label: "all_certifications_page",
  });
}

/**
 * Reads UTM / Google Ads click identifiers from the current URL.
 * Used to attribute leads to the exact campaign, ad group and keyword.
 * @returns {Record<string, string>}
 */
export function getCampaignParams() {
  const params = new URLSearchParams(window.location.search);
  const keys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
  ];

  return keys.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
}

// ── Scroll & Time Tracking (auto-initialized) ──

let scrollTracked = false;
let timeTracked = false;

export function initAutoTracking() {
  // Deep scroll tracking (75%)
  const handleScroll = () => {
    if (scrollTracked) return;
    const scrollPercent =
      (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
    if (scrollPercent >= 0.75) {
      scrollTracked = true;
      trackEvent("scroll", {
        event_category: "engagement",
        event_label: "deep_scroll_75",
        value: 1,
      });
    }
  };
  window.addEventListener("scroll", handleScroll, { passive: true });

  // Time on page tracking (30 seconds = engaged user)
  setTimeout(() => {
    if (!timeTracked) {
      timeTracked = true;
      trackEvent("time_on_page", {
        event_category: "engagement",
        event_label: "30_seconds",
        value: 1,
      });
    }
  }, 30000);

  // Track page view with UTM params (important for Google Ads attribution)
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get("utm_source");
  if (utmSource) {
    trackEvent("campaign_visit", {
      event_category: "acquisition",
      utm_source: utmSource,
      utm_medium: urlParams.get("utm_medium") || "",
      utm_campaign: urlParams.get("utm_campaign") || "",
      utm_term: urlParams.get("utm_term") || "",
      utm_content: urlParams.get("utm_content") || "",
    });
  }
}
